import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  X,
  Plus,
  Search,
  MessageCircle,
  Clock,
  Trash2,
  Loader,
  GripVertical,
} from "lucide-react";

const ChatSidebar = ({
  isOpen,
  onClose,
  searchQuery,
  onSearchChange,
  chatHistory,
  filteredHistory,
  loadingHistory,
  refreshingHistory,
  sessionId,
  onNewChat,
  onLoadChat,
  onDeleteChat,
  onRefreshHistory,
  hasMoreChats,
  loadMoreChats,
}) => {
  const [loadingChatId, setLoadingChatId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const resizeRef = useRef(null);
  const isResizing = useRef(false);

  const groupChatsByTime = (chats) => {
    const now = new Date();
    const oneHourAgo = new Date(now - 60 * 60 * 1000);
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);

    const groups = {
      lastHour: [],
      lastDay: [],
      lastWeek: [],
      older: [],
    };

    chats.forEach((chat) => {
      const chatDate = chat.timestamp;
      if (chatDate >= oneHourAgo) {
        groups.lastHour.push(chat);
      } else if (chatDate >= oneDayAgo) {
        groups.lastDay.push(chat);
      } else if (chatDate >= sevenDaysAgo) {
        groups.lastWeek.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  };

  const groupedChats = groupChatsByTime(filteredHistory);

  const handleLoadMoreChats = useCallback(async () => {
    if (loadingMore || !hasMoreChats || searchQuery || !loadMoreChats) return;

    setLoadingMore(true);
    try {
      const result = await loadMoreChats();
      if (!result || !result.success) {
        console.log("No more chats to load");
      }
    } catch (error) {
      console.error("Failed to load more chats:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreChats, searchQuery, loadMoreChats]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const options = {
      root: scrollContainerRef.current,
      rootMargin: "100px",
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting) {
        handleLoadMoreChats();
      }
    }, options);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMoreChats]);

  useEffect(() => {
    const sentinel = document.getElementById("scroll-sentinel");
    if (sentinel && observerRef.current && isOpen) {
      observerRef.current.observe(sentinel);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [groupedChats, isOpen]);

  // Resizable functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;

      const newWidth = e.clientX;
      if (newWidth >= 240 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    if (isResizing.current) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseDown = () => {
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleLoadChat = async (chatId) => {
    setLoadingChatId(chatId);
    try {
      await onLoadChat(chatId);
    } finally {
      setLoadingChatId(null);
    }
  };

  const renderChatGroup = (chats, title) => {
    if (chats.length === 0) return null;

    return (
      <div className="mb-4">
        <div className="text-x text-gray-500 mb-2 px-1 text-left">
          {title}
        </div>
        <div className="space-y-2">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`relative group p-3 bg-white hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200 ${
                sessionId === chat.id ? "bg-gray-50 border-electric-blue" : ""
              } ${loadingChatId === chat.id ? "opacity-50" : ""}`}
              onClick={() => handleLoadChat(chat.id)}
            >
              {loadingChatId === chat.id && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                  <div className="animate-spin h-4 w-4 border-b-2 border-electric-blue"></div>
                </div>
              )}
              <div className=" text-gray-900 mb-1 truncate pr-6 text-left">
                {chat.name}
              </div>
              <div className="text-sm text-gray-600 truncate text-left">
                {chat.lastMessage}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  {chat.timestamp.toLocaleDateString()}
                </div>
                <span>{chat.messageCount} messages</span>
              </div>

              <button
                onClick={(e) => onDeleteChat(chat.id, e)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-n5 hover:text-red-500 transition-all p-1 hover:bg-red-50"
                title="Delete chat"
                disabled={loadingChatId === chat.id}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`
          ${isOpen ? "" : "w-0"}
          ${isOpen ? "lg:translate-x-0" : "lg:-translate-x-full"}
          ${
            isOpen && window.innerWidth < 1024
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
          fixed lg:relative z-50 h-full
          transition-all duration-300 ease-in-out
          bg-white border-r border-gray-200
          flex flex-col overflow-hidden shadow-lg
        `}
        style={{ width: isOpen ? `${sidebarWidth}px` : "0px" }}
      >
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="text-n5 hover:text-gray-600 hover:bg-gray-200 p-1 transition-colors lg:hidden"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <button
            onClick={onNewChat}
            className="w-full flex items-center gap-2 bg-electric-blue text-white py-2 px-4 hover:bg-electric-blue-eb3 transition-colors"
          >
            <Plus size={18} />
            New chat
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-n5"
              size={18}
            />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue"
            />
          </div>

          {/* <button
            onClick={onRefreshHistory}
            disabled={refreshingHistory}
            className="mt-2 w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-black py-1 px-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={refreshingHistory ? "animate-spin" : ""}
            />
            {refreshingHistory ? "Refreshing..." : "Refresh History"}
          </button> */}
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center justify-between text-sm text-gray-700 mb-3">
            {/* <div className="flex items-center gap-2">
              <History size={16} />
              Recent ({filteredHistory.length})
            </div> */}
            {(loadingHistory || refreshingHistory) && (
              <Loader size={14} className="animate-spin text-gray-500" />
            )}
          </div>

          {filteredHistory.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageCircle size={48} className="mx-auto mb-3 text-n5" />
              <p className="text-gray-600 mb-2">
                {loadingHistory
                  ? "Loading conversations..."
                  : searchQuery
                  ? "No conversations match your search"
                  : "No conversations found"}
              </p>
              {searchQuery && (
                <button
                  onClick={() => onSearchChange("")}
                  className="text-sm text-black hover:text-gray-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              {!searchQuery ? (
                <>
                  {renderChatGroup(groupedChats.lastHour, "Last hour")}
                  {renderChatGroup(groupedChats.lastDay, "Last 24 hours")}
                  {renderChatGroup(groupedChats.lastWeek, "Last 7 days")}
                  {renderChatGroup(groupedChats.older, "Older")}
                </>
              ) : (
                <div className="space-y-2">
                  {filteredHistory.map((chat) => (
                    <div
                      key={chat.id}
                      className={`relative group p-3 bg-white hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200 ${
                        sessionId === chat.id ? "bg-gray-50 border-electric-blue" : ""
                      } ${loadingChatId === chat.id ? "opacity-50" : ""}`}
                      onClick={() => handleLoadChat(chat.id)}
                    >
                      {loadingChatId === chat.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                          <div className="animate-spin h-4 w-4 border-b-2 border-electric-blue"></div>
                        </div>
                      )}

                      <div className=" text-gray-900 mb-1 truncate pr-6 text-left">
                        {chat.name}
                      </div>
                      <div className="text-sm text-gray-600 truncate text-left">
                        {chat.lastMessage}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          {chat.timestamp.toLocaleDateString()}
                        </div>
                        <span>{chat.messageCount} messages</span>
                      </div>

                      <button
                        onClick={(e) => onDeleteChat(chat.id, e)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-n5 hover:text-red-500 transition-all p-1 hover:bg-red-50"
                        title="Delete chat"
                        disabled={loadingChatId === chat.id}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!searchQuery && hasMoreChats && (
                <div id="scroll-sentinel" className="py-4 text-center">
                  {loadingMore ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader size={16} className="animate-spin" />
                      <span className="text-sm">Loading more...</span>
                    </div>
                  ) : (
                    <div className="h-4"></div>
                  )}
                </div>
              )}

              {!searchQuery && !hasMoreChats && filteredHistory.length > 10 && (
                <div className="text-center text-xs text-n5 py-4">
                  No more conversations
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Resize Handle */}
      {isOpen && window.innerWidth >= 1024 && (
        <div
          ref={resizeRef}
          onMouseDown={handleMouseDown}
          className="hidden lg:block absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors group z-50"
          style={{ marginLeft: `${sidebarWidth}px` }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <GripVertical size={16} className="text-blue-500" />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatSidebar;
